import React, { Suspense } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Skeleton, Typography } from '@lyearn/ui';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  CertificateDimensions,
  CertificateSettings,
  CertificateTemplateType,
  CertificateType,
} from '@/types/schema';
import dynamic from '@/utils/dynamic';

const CertificatePreviewLazy = dynamic(
  () => import(/* webpackChunkName: "certificate-preview" */ './CertificatePreview'),
);

const CertLoader = ({ height, width }: { height?: number; width?: number }) => (
  <Skeleton
    height={width && height ? 256 / (width / height) : 176}
    variant="rectangular"
    width={256}
  />
);

type SettingsKeys = 'allowDownload' | 'allowLinkedinProfile' | 'allowSocialSharing';

const SETTINGS_LABEL_KEYS: Record<SettingsKeys, { admin?: string; learner: string }> = {
  allowLinkedinProfile: {
    admin: 'Certificate:ShareOnLinkedInAdmin',
    learner: 'Certificate:ShareOnLinkedIn',
  },
  allowSocialSharing: { learner: 'Certificate:PostOnSocialMedia' },
  allowDownload: { learner: 'Certificate:DownloadDirectly' },
};

const getCertificateDescription = ({
  t,
  isAdmin,
  certificateSettings,
}: {
  t: TFunction;
  certificateSettings?: CertificateSettings | null;
  isAdmin: boolean;
}) => {
  const labels = Object.entries(SETTINGS_LABEL_KEYS).reduce(
    (accumulator, [key, labels], index, entries) => {
      if (!certificateSettings?.[key as SettingsKeys]) {
        return accumulator;
      }

      const label = isAdmin && labels.admin ? labels.admin : labels.learner;

      if (accumulator.length === 0) {
        accumulator.push(t('Certificate:To', { value: t(label) }));
      } else if (index === entries.length - 1) {
        accumulator.push(t('Certificate:Or', { value: t(label) }));
      } else {
        accumulator.push(t(label));
      }
      return accumulator;
    },
    [] as string[],
  );

  const description = labels.join(', ');

  return isAdmin
    ? t('Certificate:CertificateContentDescriptionAdmin', { description })
    : t('Certificate:CertificateContentDescription', { description });
};

function ContentCertificate({
  certificateTemplate,
  issuedCertificate,
  contentName,
  className = 'mb-48',
  certificateSettings,
  externalCertificateDimensions,
  isDialogOpen,
  toggleDialog,
  certificateDescription,
}: {
  certificateTemplate?: CertificateTemplateType | null;
  issuedCertificate?: CertificateType | null;
  contentName: string;
  className?: string;
  certificateSettings?: CertificateSettings | null;
  externalCertificateDimensions?: CertificateDimensions;
  isDialogOpen?: boolean;
  toggleDialog?: (nextValue?: any) => void;
  certificateDescription?: string;
}) {
  const { t } = useTranslation('Certificate');
  const isAdmin = useAdminRoute();

  if (!certificateTemplate?.preview.url) {
    return null;
  }

  return (
    <Box className={cn('mb-32 flex-shrink xs:mb-0', className)}>
      <Typography
        className="mb-0 inline-block flex-shrink ms-4 !font-['Inter'] !text-[18px]"
        color="text-text-paragraph"
        variant={'heading-m'}>
        {t('Certificate:Certificate')}
      </Typography>
      <Typography
        className="mb-24 max-w-512 flex-shrink ms-4 !font-['Inter']"
        color={'text-text-secondary'}
        variant="body-long-01">
        {certificateDescription ??
          getCertificateDescription({
            t,
            isAdmin,
            certificateSettings,
          })}
      </Typography>
      <Suspense
        fallback={
          <CertLoader
            height={issuedCertificate?.dimensions.height}
            width={issuedCertificate?.dimensions.width}
          />
        }>
        <CertificatePreviewLazy
          certificateSettings={certificateSettings}
          certificateTemplate={certificateTemplate}
          contentName={contentName}
          externalCertificateDimensions={externalCertificateDimensions}
          isOpen={isDialogOpen}
          issuedCertificate={issuedCertificate}
          onClose={toggleDialog}
        />
      </Suspense>
    </Box>
  );
}

export default React.memo(ContentCertificate);
